import React from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { getApiClient } from '../../api/apiClient';

const InvoiceList = ({ invoices, formatCurrency, formatDate, onPreviewInvoice, onGenerateInvoice }) => {
  const { t } = useLanguage();

  // Fetch individual outgoing invoice details
  const fetchInvoiceDetails = async (invoiceId) => {
    try {
      const client = getApiClient();
      const response = await client.get(`/admin/outgoing/${invoiceId}`);

      return response.data;
    } catch (error) {
      console.error('Error fetching outgoing invoice details:', error);
      throw error;
    }
  };

  // Send invoice to ANAF
  const sendToAnaf = async (invoiceId) => {
    try {
      const client = getApiClient();
      const response = await client.post(`/admin/sendToAnaf/${invoiceId}`, {});

      alert(t('invoiceSentToAnafSuccess') || 'Invoice sent to ANAF successfully');
      return response.data;
    } catch (error) {
      console.error('Error sending invoice to ANAF:', error);
      alert(t('invoiceSentToAnafError') || 'Error sending invoice to ANAF');
      throw error;
    }
  };

  const handleSendToAnaf = async (invoiceId) => {
    if (window.confirm(t('confirmSendToAnaf') || 'Are you sure you want to send this invoice to ANAF?')) {
      await sendToAnaf(invoiceId);
    }
  };

  const handlePreviewClick = async (invoice) => {
    try {
      // For outgoing invoices, fetch detailed data from the API
      const detailedInvoice = await fetchInvoiceDetails(invoice.id);
      onPreviewInvoice({
        ...detailedInvoice,
        type: 'outgoing'
      });
    } catch (error) {
      console.error('Failed to fetch invoice details:', error);
      // Fallback to basic invoice data if detailed fetch fails
      onPreviewInvoice({
        ...invoice,
        type: 'outgoing'
      });
    }
  };

  if (invoices.length === 0) {
    return (
      <div className="bg-white shadow overflow-hidden sm:rounded-md">
        <div className="px-4 py-5 sm:px-6 bg-gray-50 border-b border-gray-200">
          <h3 className="text-lg leading-6 font-medium text-gray-900">{t('outgoingInvoices')}</h3>
          <p className="mt-1 max-w-2xl text-sm text-gray-500">
            {t('totalNumberOfInvoices')}: {invoices.length}
          </p>
        </div>
        <div className="p-6 text-center">
          <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          <h3 className="mt-2 text-sm font-medium text-gray-900">{t('noInvoices')}</h3>
          <p className="mt-1 text-sm text-gray-500">{t('createFirstInvoice')}</p>
          <button
            onClick={onGenerateInvoice}
            className="mt-3 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-purple-600 hover:bg-purple-700"
          >
            {t('generateInvoice')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white shadow overflow-hidden sm:rounded-md">
      <div className="px-4 py-5 sm:px-6 bg-gray-50 border-b border-gray-200">
        <h3 className="text-lg leading-6 font-medium text-gray-900">{t('outgoingInvoices')}</h3>
        <p className="mt-1 max-w-2xl text-sm text-gray-500">
          {t('totalNumberOfInvoices')}: {invoices.length}
        </p>
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                {t('invoiceNumber')}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                {t('clientName')}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                {t('issueDate')}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                {t('total')}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                {t('status')}
              </th>
              <th className="relative px-6 py-3">
                <span className="sr-only">{t('actions')}</span>
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {invoices.map((invoice) => (
              <tr key={invoice.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm font-mono font-medium text-gray-900">
                    {invoice.seriesNumber || invoice.invoiceNr}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-900">{invoice.clientName}</div>
                  <div className="text-sm text-gray-500">{invoice.clientEmail}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-900">{formatDate(invoice.issueDate)}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm font-medium text-gray-900">{formatCurrency(invoice.totalAmount)}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${invoice.eInvoiceStatus === 'PENDING'
                    ? 'bg-yellow-100 text-yellow-800'
                    : invoice.eInvoiceStatus === 'SENT'
                      ? 'bg-blue-100 text-blue-800'
                      : invoice.eInvoiceStatus === 'CONFIRMED'
                        ? 'bg-green-100 text-green-800'
                        : invoice.eInvoiceStatus === 'ERROR'
                          ? 'bg-red-100 text-red-800'
                          : invoice.eInvoiceStatus === 'RECEIVED'
                            ? 'bg-purple-100 text-purple-800'
                            : 'bg-gray-100 text-gray-800'
                    }`}>
                    {invoice.eInvoiceStatus === 'PENDING'
                      ? t('pending') || 'Pending'
                      : invoice.eInvoiceStatus === 'SENT'
                        ? t('sent') || 'Sent'
                        : invoice.eInvoiceStatus === 'CONFIRMED'
                          ? t('confirmed') || 'Confirmed'
                          : invoice.eInvoiceStatus === 'ERROR'
                            ? t('error') || 'Error'
                            : invoice.eInvoiceStatus === 'RECEIVED'
                              ? t('received') || 'Received'
                              : invoice.eInvoiceStatus || t('unknown') || 'Unknown'}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  <button
                    onClick={() => handlePreviewClick(invoice)}
                    className="text-purple-600 hover:text-purple-900 mr-4"
                  >
                    {t('view')}
                  </button>
                  <button className="text-blue-600 hover:text-blue-900 mr-4">
                    {t('downloadPdf')}
                  </button>
                  <button
                    onClick={() => handleSendToAnaf(invoice.id)}
                    className="text-green-600 hover:text-green-900"
                  >
                    {t('sendToAnaf') || 'Send to ANAF'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default InvoiceList;
